/**
 * useNFTPurchase - Business logic hook for NFT purchasing
 */

import { useState, useCallback, useRef } from 'react';
import { fundRaisin } from '../contracts/fundraisin';
import { useAppContext } from '../context/AppContext';
import { parseErrorMessage } from '../utils/errorUtils';
import { logError, logInfo } from '../utils/log';

export const useNFTPurchase = ({
  siteDetails,
  connectWallet,
  isWalletConnected,
  navigate,
  mountedRef
}) => {
  const { themeConfig, refreshNftData, setError } = useAppContext();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const purchaseTimeoutRef = useRef(null);

  const purchaseNFT = useCallback(async (card) => {
    if (!mountedRef.current) return;
    
    setIsPurchasing(true);
    
    try {
      const { tokenId, siteTokenIndex, price } = card;
      
      // Check wallet connection
      let walletStatus = isWalletConnected;
      if (!isWalletConnected) {
        walletStatus = await connectWallet();
      }
      
      if (!walletStatus) {
        setIsPurchasing(false);
        return;
      }

      // Execute purchase
      const message = await fundRaisin.buyNft(
        siteDetails.siteId,
        siteTokenIndex,
        tokenId,
        price
      );

      if (!mountedRef.current) return;

      

      // Delayed navigation with cleanup
      purchaseTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          // Force update and navigate
          refreshNftData();
          logInfo(message);
          setIsPurchasing(false);
          navigate("/my");
        }
      }, 2000);

    } catch (err) {
      if (!mountedRef.current) return;
      
      setIsPurchasing(false);
      logError(err);
      
      const errorMessage = parseErrorMessage(err, themeConfig, 'buy');
      setError(errorMessage);
    }
  }, [
    siteDetails?.siteId,
    connectWallet,
    isWalletConnected,
    navigate,
    themeConfig,
    refreshNftData,
    setError,
    mountedRef
  ]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (purchaseTimeoutRef.current) {
      clearTimeout(purchaseTimeoutRef.current);
      purchaseTimeoutRef.current = null;
    }
  }, []);

  return {
    purchaseNFT,
    isPurchasing,
    cleanup,
  };
};
